import axios from 'axios'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import _ from 'lodash'
import Lottie from 'lottie-react'
import React, { useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { SingleReceiptReminderSuccessToastMessage } from 'src/app/Toast/Toast'
import receiptReminderAnimation from 'src/assets/animations/ReceiptReminder.json'
import SvgReceiptMissing from 'src/components/Svgs/SvgReceiptMissing'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { useRequestConfig } from 'src/queries/queries.utils'
import { css } from 'styled-components'

interface SingleReceiptReminderProps {
  transactionId: string
  name?: string
  wrapText?: boolean
  maxWidth?: number
}

const lottieStyle = {
  display: 'flex',
  alignItems: 'center',
  width: 24,
  height: 24,
  transform: 'scale(calc(20 / 24))',
}

const SingleReceiptReminder: React.FC<SingleReceiptReminderProps> = (
  props,
): React.ReactElement => {
  const authedJsonApiRequestConfig = useRequestConfig()
  const { analytics } = useAnalyticsContext()

  const [isDisabled, setIsDisabled] = useState<boolean>(false)

  const dispatch = useBackgroundDispatch()
  const translate = useTranslate()

  const handleTrackReceiptReminder = (reminderSent: boolean): void => {
    analytics?.track('Transactions - Receipt Reminder - button press', {
      reminder_type: 'single notification',
      reminder_sent: reminderSent,
    })
  }

  const handleOnClick = (e: React.MouseEvent): void => {
    e.preventDefault()

    // Isolated axios call since no redux is needed and the endpoint is only used here
    if (!isDisabled) {
      axios
        .post(
          `/transactions/${props.transactionId}/receiptreminder`,
          {},
          authedJsonApiRequestConfig,
        )
        .then(
          () => {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <SingleReceiptReminderSuccessToastMessage name={props.name} />
                ),
              }),
            )
            handleTrackReceiptReminder(true)
            setIsDisabled(true)
          },
          (err) => {
            const error = _.get(err, 'response.data.error', undefined)
            error !== undefined &&
              dispatch(
                createSetIsToastOpen(true, {
                  toastMessage: <span>{error}</span>,
                  failureColorScheme: true,
                  hideImage: true,
                }),
              )
            handleTrackReceiptReminder(false)
            setIsDisabled(true)
          },
        )
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      onClick={handleOnClick}
      role={'button'}
      tabIndex={0}
      data-testid={
        isDisabled ? 'sentReceiptReminderIcon' : 'missingReceiptsBtn'
      }
    >
      {
        <TooltipIcon
          icon={
            <SvgReceiptMissing
              css={css`
                transform: scale(calc(20 / 24));
              `}
            />
          }
          iconHover={
            <Lottie
              loop={2}
              animationData={receiptReminderAnimation}
              style={lottieStyle}
            />
          }
        >
          {translate(
            'Remind this person to attach a receipt for this transaction',
          )}
        </TooltipIcon>
      }
    </div>
  )
}

export default SingleReceiptReminder
